/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  reaction,
  withAssign,
} from '@reatom/framework'
import i18next from 'i18next'

import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'
import { isAdminAtom } from '@root/entities/auth'
import { supportTicketUpdatedEvent } from '@root/entities/realtime'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { reatomResourcePaginated, reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { SupportTicket } from '../model/support-ticket'

import { supportDialogScreenAtom } from './reatom-support-dialog'
import {
  fetchSupportAdminTicketsRequestAction,
  fetchSupportAdminTicketsUnreadRequestAction,
  updateSupportAdminTicketStatusRequestAction,
} from './support-requests-admin'

import type { SupportTicketAdminType } from '../model/support-ticket'
import type { tradeApi } from '@blade/socket-types'
import type { PaginatedData } from '@root/shared/lib'

export const supportTicketStatusToggleAdminEvent = action<{
  ticket: SupportTicket
}>('supportTicketStatusToggleAdminEvent')

const reatomSupportTicketsAdmin = () => {
  const ticketType = atom<SupportTicketAdminType>('new')
  const ticketsState = reatomResourcePaginated(
    async (ctx, { limit, offset }) => {
      const status = ctx.spy(ticketType)

      const result = await ctx.schedule(() =>
        fetchSupportAdminTicketsRequestAction(ctx, {
          limit,
          offset,
          status,
        }),
      )

      return result.pipe(
        Result.map(
          (it): PaginatedData<SupportTicket, 'offset'> => ({
            data: it.data.map((dto) => new SupportTicket(dto)),
          }),
        ),
      )
    },
    {
      name: 'ticketsState',
      loadOnConnect: true,
      limit: 20,
      strategy: 'offset',
      // `onDisconnect` tears down the async reaction; with default `reconnectStrategy`
      // (`none`), reopening the dialog skips `runAction` while state is still Success, so
      // nothing listens to `ticketType` anymore — tabs appear frozen. Refresh restarts it.
      reconnectStrategy: 'refresh',
    },
  )

  const adminTicketsUnreadTotalsRequest = reatomRequest({
    execAction: fetchSupportAdminTicketsUnreadRequestAction,
    name: 'adminTicketsUnreadTotals',
  })

  const totals = atom((ctx) => {
    const state = ctx.spy(adminTicketsUnreadTotalsRequest)
    if (!AsyncStateN.isSuccess(state)) {
      return {
        unreadTickets: 0,
        unreadMessages: 0,
        newTickets: 0,
        activeTickets: 0,
        closedTickets: 0,
      }
    }
    return state.value
  })

  const upsertTicket = action((ctx, ticketDto: tradeApi.SupportTicketData) => {
    const state = ctx.get(ticketsState)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    const existingTicket = state.value.data.find((it) => it.id === ticketDto.id)
    if (existingTicket) {
      existingTicket.updateFromDto(ctx, ticketDto)
      return
    }

    const ticket = new SupportTicket(ticketDto)
    const updatedTickets = pipe(
      state.value.data,
      Array.append(ticket),
      Array.sort(SupportTicket.cmpByUpdatedAt(ctx)),
    )
    ticketsState.setSuccess(ctx, {
      ...state.value,
      data: updatedTickets,
    })
  })

  onConnect(adminTicketsUnreadTotalsRequest, (ctx) =>
    adminTicketsUnreadTotalsRequest.execute(ctx),
  )

  const toggleTicketStatusAction = action(async (ctx, { ticket }) => {
    const currentStatus = ctx.get(ticket.status)

    const newStatus = currentStatus === 'active' ? 'closed' : 'active'
    const requestState = await ctx.schedule(() =>
      updateSupportAdminTicketStatusRequestAction(ctx, {
        ticketId: ticket.id,
        status: newStatus,
      }),
    )

    if (Result.isFailure(requestState)) {
      showErrorSnackbarAction(ctx, requestState.failure)
      return
    }

    const { status, subject } = requestState.success

    ticketsState.refresh(ctx)
    adminTicketsUnreadTotalsRequest.refresh(ctx)
    supportDialogScreenAtom(ctx, { screen: 'tickets' })

    showSnackbarAction(ctx, {
      text:
        status === 'closed'
          ? i18next.t('support.ticket-status-updated.closed', { subject })
          : i18next.t('support.ticket-status-updated.active', { subject }),
      intent: 'success',
    })
  })

  return ticketsState.pipe(
    withAssign(() => ({
      upsertTicket,
      ticketType,
      adminTicketsUnreadTotalsRequest,
      totals,
      toggleTicketStatusAction,
    })),
  )
}

export const supportTicketsModelAdmin = reatomSupportTicketsAdmin()

export const initializeSupportTicketsAdmin = reaction(async (ctx) => {
  const isAdmin = ctx.spy(isAdminAtom)

  if (!isAdmin) {
    return
  }

  const { mainWebsocket } = ctx.get(dependenciesAtom)
  mainWebsocket.supportTicketUpdatesSubscribeAction(ctx, {})

  supportTicketsModelAdmin.run(ctx)
  supportTicketUpdatedEvent.onCall((ctx, data) => {
    supportTicketsModelAdmin.upsertTicket(ctx, data)
  })
  supportTicketStatusToggleAdminEvent.onCall((ctx, { ticket }) =>
    supportTicketsModelAdmin.toggleTicketStatusAction(ctx, { ticket }),
  )
}, 'initializeSupportTicketsAdmin')
